import type { Fragment, Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags';

/**
 * When `replaceNode` replaces a block node (e.g. paragraph) with another block of
 * the same type but a different `localId`, ProseMirror's `replaceRange` merges the
 * inline content into the existing block — preserving the original block's attrs
 * (including `localId`). The same problem applies when the replacement is a slice
 * containing multiple top-level blocks: each merged block keeps its original
 * `localId` instead of the one supplied by the LLM.
 *
 * This helper walks every top-level node in `replacementContent` and patches the
 * `localId` attr on the corresponding block in the doc when it differs. The
 * doc is walked sequentially starting from `startPos`, advancing by each inserted
 * node's size, so multi-node replacements are handled correctly.
 *
 * The fix is gated behind `platform_editor_ai_replace_node_localid_fix`.
 */
export function applyReplaceNodeLocalIdFix({
	tr,
	replacementContent,
	startPos,
}: {
	replacementContent: Fragment | null | undefined;
	startPos: number;
	tr: Transaction;
}): Transaction {
	if (!fg('platform_editor_ai_replace_node_localid_fix')) {
		return tr;
	}

	if (!replacementContent || replacementContent.childCount === 0) {
		return tr;
	}

	const docSize = tr.doc.content.size;
	if (startPos < 0 || startPos > docSize) {
		return tr;
	}

	// First, try to align startPos to the first top-level block we want to patch.
	// `replaceRange` typically lands the caller's `startPos` inside the first
	// merged block, so we resolve up to find that block's opening position and
	// then advance through each subsequent top-level block.
	let cursor = findFirstBlockPos(tr, startPos, replacementContent.firstChild?.type);
	if (cursor === undefined) {
		return tr;
	}

	for (let i = 0; i < replacementContent.childCount; i++) {
		const replacementChild = replacementContent.child(i);
		if (cursor < 0 || cursor >= tr.doc.content.size) {
			break;
		}

		let resolved;
		try {
			resolved = tr.doc.resolve(cursor);
		} catch {
			break;
		}

		const docNode = resolved.nodeAfter;
		if (!docNode) {
			break;
		}

		// Types must match — if the LLM streamed a slice whose shape no longer
		// matches the doc we shouldn't blindly rewrite the wrong node.
		if (docNode.type !== replacementChild.type) {
			cursor += docNode.nodeSize;
			continue;
		}

		const replacementLocalId: unknown = replacementChild.attrs?.localId;
		const currentLocalId: unknown = docNode.attrs?.localId;

		if (
			typeof replacementLocalId === 'string' &&
			replacementLocalId.length > 0 &&
			currentLocalId !== replacementLocalId &&
			docNode.attrs &&
			'localId' in docNode.attrs
		) {
			try {
				tr = tr.setNodeMarkup(
					cursor,
					undefined,
					{ ...docNode.attrs, localId: replacementLocalId },
					docNode.marks,
				);
			} catch {
				// Swallow — keep advancing so we still patch the remaining blocks.
			}
		}

		cursor += docNode.nodeSize;
	}

	return tr;
}

/**
 * `replaceRange` typically leaves `startPos` pointing inside the first merged
 * block (e.g. at the start of its text content). Walk up the resolved position
 * to find the opening position of the first node matching `firstChildType`.
 */
function findFirstBlockPos(
	tr: Transaction,
	startPos: number,
	firstChildType: PMNode['type'] | undefined,
): number | undefined {
	const docSize = tr.doc.content.size;
	let resolved;
	try {
		resolved = tr.doc.resolve(Math.min(Math.max(startPos, 0), docSize));
	} catch {
		return undefined;
	}

	// First, try walking up from the resolved position — handles the common case
	// where `startPos` landed inside the merged block.
	for (let depth = resolved.depth; depth >= 0; depth--) {
		const nodeAtDepth = resolved.node(depth);
		if (!nodeAtDepth) {
			continue;
		}
		if (firstChildType && nodeAtDepth.type !== firstChildType) {
			continue;
		}
		if (depth === 0) {
			// At doc level, fall through to `nodeAfter` check below.
			break;
		}
		return resolved.before(depth);
	}

	// Otherwise the position may already be at the block boundary — return as-is
	// so the caller's `nodeAfter` lookup can drive the walk.
	return resolved.pos;
}
